// src/components/LoginScreen.js
//
// Full-screen login. Magic link (e-mail) + Google; Apple shows as
// "coming soon" until the developer account exists (APPLE_ENABLED flag).
// Invite-only beta: a new address needs a code, an existing one does not.

import React, { useEffect, useState } from "react";
import { api, startOAuth } from "../api";

const ERROR_TEXT = {
  invite_required: "This e-mail is not on the beta yet. Enter your invite code.",
  invite_invalid: "That invite code is not valid or has been used.",
  invalid_email: "Please enter a valid e-mail address.",
  mail_failed: "The login mail could not be sent. Try again in a minute.",
  expired: "That link has expired or was already used. Request a new one.",
  missing_token: "That link is incomplete. Request a new one.",
  google_denied: "Google sign-in was cancelled.",
  google_failed: "Google sign-in failed. Try the e-mail link instead.",
  bad_state: "The sign-in session expired. Try again.",
  email_unverified: "Google reports this e-mail as unverified.",
  invalid_code: "That code is not valid. Check the mail or request a new one.",
};

function BetaGate({ onUnlocked }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/api/beta/unlock", { password: pw });
      onUnlocked();
    } catch (err) {
      setError(err.body && err.body.error === "wrong_password" ? "Wrong password." : err.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="loginScreen">
      <div className="loginScreen__inner">
        <img src="/logo.svg" height="40" alt="RESET Ultra" className="loginScreen__logo" />
        <h1 className="loginScreen__title">Private beta</h1>
        <p className="loginScreen__sub">Enter the beta password to continue.</p>
        <form className="loginScreen__card" onSubmit={submit}>
          <label className="loginScreen__label" htmlFor="beta-pw">Beta password</label>
          <input id="beta-pw" className="input" type="password" autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} required />
          {error && <div className="loginScreen__error">{error}</div>}
          <button type="submit" className="btn btnOk loginScreen__primary" disabled={busy || !pw}>{busy ? "…" : "Continue"}</button>
        </form>
      </div>
    </div>
  );
}

export default function LoginScreen({ auth, initialError, betaLocked, onBetaUnlocked }) {
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [error, setError] = useState(initialError ? ERROR_TEXT[initialError] || initialError : "");

  useEffect(() => {
    if (initialError === "invite_required" || initialError === "invite_invalid") setShowInvite(true);
  }, [initialError]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/api/auth/magic-link", { email: email.trim(), inviteCode: invite.trim() || undefined });
      setSent(true);
      setCode("");
      if (r.devLink) setDevLink(r.devLink);
      if (r.devCode) setCode(r.devCode);
    } catch (err) {
      const code = err.body && err.body.error;
      if (code === "invite_required" || code === "invite_invalid") setShowInvite(true);
      setError(ERROR_TEXT[code] || err.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  // Sign in with the 6-digit code from the same mail — the path inside the
  // native app, where the link would open the system browser instead.
  const submitCode = async (e) => {
    e.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6 || codeBusy) return;
    setCodeBusy(true);
    setError("");
    try {
      await api.post("/api/auth/verify-code", { email: email.trim(), code: digits });
      window.location.replace("/?login=ok");
    } catch (err) {
      const c = err.body && err.body.error;
      setError(ERROR_TEXT[c] || err.message || "Login failed.");
      setCodeBusy(false);
    }
  };

  const google = () => {
    const q = invite.trim() ? `?invite=${encodeURIComponent(invite.trim())}` : "";
    startOAuth(`/api/auth/google${q}`);
  };

  if (betaLocked) return <BetaGate onUnlocked={onBetaUnlocked} />;

  return (
    <div className="loginScreen">
      <div className="loginScreen__inner">
        <img src="/logo.svg" height="40" alt="RESET Ultra" className="loginScreen__logo" />
        <h1 className="loginScreen__title">Training plans for ultra distances</h1>
        <p className="loginScreen__sub">Sign in to keep your goals, plan and coach history on every device.</p>

        {sent ? (
          <div className="loginScreen__card">
            <div className="loginScreen__cardTitle">Check your inbox</div>
            <p className="loginScreen__cardText">
              We sent a sign-in link and a 6-digit code to <b>{email.trim()}</b>. Both work once and expire in 15 minutes.
            </p>
            <form onSubmit={submitCode} style={{ marginTop: 6 }}>
              <label className="loginScreen__label" htmlFor="login-code">Code from the mail</label>
              <input
                id="login-code"
                className="input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
                style={{ letterSpacing: 4, fontVariantNumeric: "tabular-nums" }}
              />
              {error && <div className="loginScreen__error">{error}</div>}
              <button type="submit" className="btn btnOk loginScreen__primary" disabled={codeBusy || code.replace(/\D/g, "").length !== 6}>
                {codeBusy ? "Signing in…" : "Sign in with code"}
              </button>
            </form>
            {devLink && (
              <a className="btn btnOk" href={devLink} style={{ marginTop: 8 }}>
                Open dev link
              </a>
            )}
            <button type="button" className="loginScreen__link" onClick={() => { setSent(false); setDevLink(null); }}>
              Use a different address
            </button>
          </div>
        ) : (
          <form className="loginScreen__card" onSubmit={submit}>
            <label className="loginScreen__label" htmlFor="login-email">E-mail</label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {showInvite ? (
              <>
                <label className="loginScreen__label" htmlFor="login-invite" style={{ marginTop: 10 }}>Invite code</label>
                <input
                  id="login-invite"
                  className="input"
                  type="text"
                  autoCapitalize="characters"
                  placeholder="RESET-XXXX-XXXX"
                  value={invite}
                  onChange={(e) => setInvite(e.target.value.toUpperCase())}
                />
              </>
            ) : (
              <button type="button" className="loginScreen__link" onClick={() => setShowInvite(true)}>
                Have an invite code?
              </button>
            )}
            {error && <div className="loginScreen__error">{error}</div>}
            <button type="submit" className="btn btnOk loginScreen__primary" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Send sign-in link"}
            </button>

            <div className="loginScreen__divider"><span>or</span></div>

            {auth?.google ? (
              <button type="button" className="btn btnGhost loginScreen__provider" onClick={google}>
                Continue with Google
              </button>
            ) : (
              <button type="button" className="btn btnGhost loginScreen__provider" disabled title="Google login is not configured on this server">
                Continue with Google
              </button>
            )}
            <button type="button" className="btn btnGhost loginScreen__provider" disabled>
              Continue with Apple <span className="loginScreen__soon">coming soon</span>
            </button>
          </form>
        )}

        <p className="loginScreen__foot">
          After signing in, bring your runs from Strava, Apple Health, Health Connect or a .fit file. Private beta, invite only.
        </p>
      </div>
    </div>
  );
}
